/**
 * Password rules for new passwords (sign-up and reset). They mirror the
 * project's Supabase Auth settings (Authentication → Sign In / Providers →
 * Email): at least 12 characters with a lowercase letter, an uppercase
 * letter, a digit and a symbol. Supabase enforces them on the server; this
 * shows people what's needed before they submit. Change both together.
 */

export const PASSWORD_MIN_LENGTH = 12;

/** The symbols Supabase Auth counts (anything else, e.g. "é" or a space, doesn't). */
export const PASSWORD_SYMBOLS = '!@#$%^&*()_+-=[]{};\'\\:"|<>?,./`~';

export interface PasswordRule {
  id: 'length' | 'lower' | 'upper' | 'digit' | 'symbol';
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (p) => p.length >= PASSWORD_MIN_LENGTH },
  { id: 'lower', label: 'A lowercase letter (a–z)', test: (p) => /[a-z]/.test(p) },
  { id: 'upper', label: 'An uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
  { id: 'digit', label: 'A number (0–9)', test: (p) => /[0-9]/.test(p) },
  { id: 'symbol', label: 'A symbol, like ! ? # @ $ %', test: (p) => [...p].some((c) => PASSWORD_SYMBOLS.includes(c)) },
];

const inSentence = (label: string) => label.charAt(0).toLowerCase() + label.slice(1);

/** Each rule with whether the password meets it, in display order. */
export function checkPassword(password: string): { rule: PasswordRule; met: boolean }[] {
  return PASSWORD_RULES.map((rule) => ({ rule, met: rule.test(password) }));
}

/** A sentence naming what's still missing, or null when the password is strong enough. */
export function passwordProblem(password: string): string | null {
  const missing = checkPassword(password).filter((c) => !c.met).map((c) => inSentence(c.rule.label));
  if (missing.length === 0) return null;
  return `Your password needs: ${missing.join(', ')}.`;
}

/** Supabase's "weak_password" error, in words people understand. */
export function friendlyPasswordError(err: unknown): string | null {
  const e = err as { code?: string; message?: string; name?: string } | null;
  if (!e) return null;
  if (e.code === 'weak_password' || e.name === 'AuthWeakPasswordError' || /weak|password should/i.test(e.message ?? '')) {
    return `Please choose a stronger password: ${PASSWORD_RULES.map((r) => inSentence(r.label)).join(', ')}.`;
  }
  return null;
}
